import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Shield, 
  Plus, 
  Trash2, 
  Eye, 
  Camera,
  X,
  Upload,
  Calendar,
  Store,
  Euro,
  Package,
  Image as ImageIcon,
  Pencil
} from 'lucide-react';

const Warranties = () => {
  const [warranties, setWarranties] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedWarranty, setSelectedWarranty] = useState(null);
  const [newWarranty, setNewWarranty] = useState({
    product_name: '',
    shop_id: '',
    purchase_date: '',
    price: '',
    receipt_image: null
  });
  const [editWarranty, setEditWarranty] = useState({
    product_name: '',
    shop_id: '',
    purchase_date: '',
    price: '',
    receipt_image: null
  });
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [warrantiesRes, shopsRes] = await Promise.all([
        api.get('/warranties'),
        api.get('/shops')
      ]);
      setWarranties(warrantiesRes.data);
      setShops(shopsRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e, isEdit = false) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Immagine troppo grande (max 5MB)');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      if (isEdit) {
        setEditWarranty(prev => ({ ...prev, receipt_image: reader.result }));
      } else {
        setNewWarranty(prev => ({ ...prev, receipt_image: reader.result }));
      }
    };
    reader.readAsDataURL(file);
  };

  const handleAddWarranty = async () => {
    if (!newWarranty.product_name.trim()) {
      toast.error('Inserisci il nome del prodotto');
      return;
    }
    if (!newWarranty.shop_id) {
      toast.error('Seleziona il negozio');
      return;
    }
    if (!newWarranty.purchase_date) {
      toast.error('Inserisci la data di acquisto');
      return;
    }
    if (!newWarranty.price || parseFloat(newWarranty.price) <= 0) {
      toast.error('Inserisci un prezzo valido');
      return;
    }

    try {
      await api.post('/warranties', {
        ...newWarranty,
        price: parseFloat(newWarranty.price),
        purchase_date: new Date(newWarranty.purchase_date).toISOString()
      });
      toast.success('Garanzia aggiunta con successo');
      setAddDialogOpen(false);
      setNewWarranty({
        product_name: '',
        shop_id: '',
        purchase_date: '',
        price: '',
        receipt_image: null
      });
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'aggiunta della garanzia');
    }
  };

  const handleEditWarranty = async () => {
    if (!editWarranty.product_name.trim()) {
      toast.error('Inserisci il nome del prodotto');
      return;
    }

    try {
      await api.put(`/warranties/${selectedWarranty.id}`, {
        ...editWarranty,
        price: parseFloat(editWarranty.price),
        purchase_date: new Date(editWarranty.purchase_date).toISOString()
      });
      toast.success('Garanzia aggiornata con successo');
      setEditDialogOpen(false);
      setSelectedWarranty(null);
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento della garanzia');
    }
  };

  const handleDeleteWarranty = async () => {
    try {
      await api.delete(`/warranties/${selectedWarranty.id}`);
      toast.success('Garanzia eliminata');
      setDeleteDialogOpen(false);
      setSelectedWarranty(null);
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione della garanzia');
    }
  };

  const openViewDialog = (warranty) => {
    setSelectedWarranty(warranty);
    setViewDialogOpen(true);
  };

  const openEditDialog = (warranty) => {
    setSelectedWarranty(warranty);
    setEditWarranty({
      product_name: warranty.product_name,
      shop_id: warranty.shop_id,
      purchase_date: warranty.purchase_date.split('T')[0],
      price: warranty.price.toString(),
      receipt_image: warranty.receipt_image
    });
    setEditDialogOpen(true);
  };

  const getShopName = (shopId) => {
    const shop = shops.find(s => s.id === shopId);
    return shop ? shop.name : 'N/A';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D94E1F]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Garanzie
          </h1>
          <p className="text-slate-500 mt-1">Gestisci le garanzie dei tuoi acquisti</p>
        </div>
        <Button
          onClick={() => {
            setNewWarranty({
              product_name: '',
              shop_id: '',
              purchase_date: '',
              price: '',
              receipt_image: null
            });
            setAddDialogOpen(true);
          }}
          className="bg-[#D94E1F] hover:bg-[#B83E15] text-white"
          data-testid="add-warranty-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuova Garanzia
        </Button>
      </div>

      {/* Warranties List */}
      {warranties.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 p-12 text-center">
          <Shield className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">Nessuna garanzia</h3>
          <p className="text-slate-500 mb-4">Aggiungi la tua prima garanzia</p>
          <Button
            onClick={() => setAddDialogOpen(true)}
            className="bg-[#D94E1F] hover:bg-[#B83E15] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Aggiungi Garanzia
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
          <div className="divide-y divide-slate-100">
            {warranties.map((warranty) => (
              <div key={warranty.id} className="p-4 hover:bg-slate-50 transition-colors">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-start gap-4">
                    {warranty.receipt_image ? (
                      <div 
                        className="w-16 h-16 rounded-lg bg-cover bg-center cursor-pointer border border-slate-200"
                        style={{ backgroundImage: `url(${warranty.receipt_image})` }}
                        onClick={() => openViewDialog(warranty)}
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center">
                        <ImageIcon className="w-6 h-6 text-slate-400" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-medium text-slate-900">{warranty.product_name}</h3>
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <Store className="w-3 h-3" />
                        {getShopName(warranty.shop_id)}
                      </p>
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(warranty.purchase_date)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-semibold text-[#D94E1F]">
                      {formatPrice(warranty.price)}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openViewDialog(warranty)}
                        className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(warranty)}
                        className="text-purple-600 border-purple-200 hover:bg-purple-50"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedWarranty(warranty);
                          setDeleteDialogOpen(true);
                        }}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Warranty Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#D94E1F]" />
              Nuova Garanzia
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-700">Nome Prodotto *</Label>
              <Input
                value={newWarranty.product_name}
                onChange={(e) => setNewWarranty({ ...newWarranty, product_name: e.target.value })}
                placeholder="es. iPhone 15, Lavatrice..."
                className="mt-1.5 h-12 rounded-xl"
                data-testid="warranty-product-input"
              />
            </div>

            <div>
              <Label className="text-slate-700">Negozio *</Label>
              <Select
                value={newWarranty.shop_id}
                onValueChange={(value) => setNewWarranty({ ...newWarranty, shop_id: value })}
              >
                <SelectTrigger className="mt-1.5 h-12 rounded-xl" data-testid="warranty-shop-select">
                  <SelectValue placeholder="Seleziona negozio" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {shops.map((shop) => (
                    <SelectItem key={shop.id} value={shop.id}>
                      {shop.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-slate-700">Data Acquisto *</Label>
              <Input
                type="date"
                value={newWarranty.purchase_date}
                onChange={(e) => setNewWarranty({ ...newWarranty, purchase_date: e.target.value })}
                className="mt-1.5 h-12 rounded-xl"
                data-testid="warranty-date-input"
              />
            </div>

            <div>
              <Label className="text-slate-700">Prezzo *</Label>
              <div className="relative mt-1.5">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="number"
                  step="0.01"
                  value={newWarranty.price}
                  onChange={(e) => setNewWarranty({ ...newWarranty, price: e.target.value })}
                  placeholder="0.00"
                  className="h-12 rounded-xl pl-10"
                  data-testid="warranty-price-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-700">Foto Scontrino</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => handleImageUpload(e, false)}
                className="hidden"
              />
              {newWarranty.receipt_image ? (
                <div className="mt-2 relative">
                  <img
                    src={newWarranty.receipt_image}
                    alt="Scontrino"
                    className="w-full h-48 object-cover rounded-xl border border-slate-200"
                  />
                  <button
                    type="button"
                    onClick={() => setNewWarranty({ ...newWarranty, receipt_image: null })}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-2 w-full h-24 rounded-xl border-dashed border-2 flex flex-col items-center justify-center gap-2"
                >
                  <Camera className="w-6 h-6 text-slate-400" />
                  <span className="text-sm text-slate-500">Scatta o carica foto</span>
                </Button>
              )}
            </div>
          </div>

          <DialogFooter className="flex flex-col gap-2">
            <Button
              onClick={handleAddWarranty}
              className="w-full h-12 bg-[#D94E1F] hover:bg-[#B83E15] text-white rounded-xl"
              data-testid="save-warranty-btn"
            >
              Salva Garanzia
            </Button>
            <Button
              variant="outline"
              onClick={() => setAddDialogOpen(false)}
              className="w-full h-12 rounded-xl"
            >
              Annulla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Warranty Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Pencil className="w-5 h-5 text-[#D94E1F]" />
              Modifica Garanzia
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-700">Nome Prodotto *</Label>
              <Input
                value={editWarranty.product_name}
                onChange={(e) => setEditWarranty({ ...editWarranty, product_name: e.target.value })}
                placeholder="es. iPhone 15, Lavatrice..."
                className="mt-1.5 h-12 rounded-xl"
              />
            </div>

            <div>
              <Label className="text-slate-700">Negozio *</Label>
              <Select
                value={editWarranty.shop_id}
                onValueChange={(value) => setEditWarranty({ ...editWarranty, shop_id: value })}
              >
                <SelectTrigger className="mt-1.5 h-12 rounded-xl">
                  <SelectValue placeholder="Seleziona negozio" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {shops.map((shop) => (
                    <SelectItem key={shop.id} value={shop.id}>
                      {shop.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-slate-700">Data Acquisto *</Label>
              <Input
                type="date"
                value={editWarranty.purchase_date}
                onChange={(e) => setEditWarranty({ ...editWarranty, purchase_date: e.target.value })}
                className="mt-1.5 h-12 rounded-xl"
              />
            </div>

            <div>
              <Label className="text-slate-700">Prezzo *</Label>
              <div className="relative mt-1.5">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="number"
                  step="0.01"
                  value={editWarranty.price}
                  onChange={(e) => setEditWarranty({ ...editWarranty, price: e.target.value })}
                  placeholder="0.00"
                  className="h-12 rounded-xl pl-10"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-700">Foto Scontrino</Label>
              <input
                ref={editFileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => handleImageUpload(e, true)}
                className="hidden"
              />
              {editWarranty.receipt_image ? (
                <div className="mt-2 relative">
                  <img
                    src={editWarranty.receipt_image}
                    alt="Scontrino"
                    className="w-full h-48 object-cover rounded-xl border border-slate-200"
                  />
                  <button
                    type="button"
                    onClick={() => setEditWarranty({ ...editWarranty, receipt_image: null })}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => editFileInputRef.current?.click()}
                  className="mt-2 w-full h-24 rounded-xl border-dashed border-2 flex flex-col items-center justify-center gap-2"
                >
                  <Camera className="w-6 h-6 text-slate-400" />
                  <span className="text-sm text-slate-500">Scatta o carica foto</span>
                </Button>
              )}
            </div>
          </div>

          <DialogFooter className="flex flex-col gap-2">
            <Button
              onClick={handleEditWarranty}
              className="w-full h-12 bg-[#D94E1F] hover:bg-[#B83E15] text-white rounded-xl"
            >
              Salva Modifiche
            </Button>
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
              className="w-full h-12 rounded-xl"
            >
              Annulla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Warranty Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-white max-w-lg">
          {selectedWarranty && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-slate-900">
                  {selectedWarranty.product_name}
                </DialogTitle>
                <DialogDescription className="text-slate-500">
                  Dettagli garanzia
                </DialogDescription>
              </DialogHeader>
              
              <div className="py-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <p className="text-xs text-slate-500 uppercase">Negozio</p>
                    <p className="font-medium text-slate-900 flex items-center gap-2 mt-1">
                      <Store className="w-4 h-4 text-[#D94E1F]" />
                      {getShopName(selectedWarranty.shop_id)}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <p className="text-xs text-slate-500 uppercase">Data Acquisto</p>
                    <p className="font-medium text-slate-900 flex items-center gap-2 mt-1">
                      <Calendar className="w-4 h-4 text-[#D94E1F]" />
                      {formatDate(selectedWarranty.purchase_date)}
                    </p>
                  </div>
                </div>
                
                <div className="p-4 bg-[#D94E1F]/10 rounded-xl">
                  <p className="text-xs text-slate-500 uppercase">Prezzo</p>
                  <p className="text-2xl font-bold text-[#D94E1F] mt-1">
                    {formatPrice(selectedWarranty.price)}
                  </p>
                </div>

                {selectedWarranty.receipt_image && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase mb-2">Foto Scontrino</p>
                    <img
                      src={selectedWarranty.receipt_image}
                      alt="Scontrino"
                      className="w-full rounded-xl border border-slate-200"
                    />
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button
                  onClick={() => setViewDialogOpen(false)}
                  className="w-full h-12 bg-[#D94E1F] hover:bg-[#B83E15] text-white rounded-xl"
                >
                  Chiudi
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-900">Elimina Garanzia</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">
              Sei sicuro di voler eliminare la garanzia per "{selectedWarranty?.product_name}"? 
              Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col gap-2 sm:flex-col">
            <Button
              onClick={handleDeleteWarranty}
              className="w-full h-12 bg-red-600 hover:bg-red-700 text-white rounded-xl"
            >
              Elimina
            </Button>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="w-full h-12 rounded-xl"
            >
              Annulla
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Warranties;
